import { useState } from "react";
import type { LiveItem } from "@/mocks/lives";
import { formatViewers, getCategoryLabel } from "@/utils/live";
import StatusBadge from "@/components/base/StatusBadge";

interface LiveInfoBarProps {
  item: LiveItem;
}

export default function LiveInfoBar({ item }: LiveInfoBarProps) {
  const [following, setFollowing] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(window.location.href);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="rounded-lg border border-background-200/60 bg-background-100 p-5">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={item.status} />
        <span className="rounded-full bg-primary-500/15 px-2.5 py-1 text-[11px] font-medium text-primary-300">
          {getCategoryLabel(item.category)}频道
        </span>
        <span className="rounded-md bg-background-200 px-2 py-1 text-[11px] text-foreground-600">
          {item.resolution}
        </span>
      </div>

      <h1 className="mt-4 font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
        {item.title}
      </h1>

      <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-500/15 font-label text-base font-bold text-primary-300">
            {item.streamer.slice(0, 1)}
          </span>
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-foreground-950">{item.streamer}</span>
            <span className="text-xs text-foreground-500">主播 · {getCategoryLabel(item.category)}分区</span>
          </div>
          <button
            type="button"
            onClick={() => setFollowing((prev) => !prev)}
            className={`ml-2 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
              following
                ? "border border-background-300 bg-background-100 text-foreground-700"
                : "bg-primary-500 text-background-50 hover:bg-primary-600"
            }`}
          >
            <i className={following ? "ri-check-line" : "ri-add-line"} />
            {following ? "已关注" : "关注"}
          </button>
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
          <span className="flex items-center gap-1.5">
            <i className="ri-eye-line text-primary-300" />
            {formatViewers(item.viewers)} 人观看
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-time-line text-primary-300" />
            {item.startTime} 开播
          </span>
          <button
            type="button"
            onClick={handleShare}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-500/60"
          >
            <i className="ri-share-line" />
            {copied ? "链接已复制" : "分享"}
          </button>
        </div>
      </div>
    </div>
  );
}